"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Device = void 0;
const iotv_v3_types_1 = require("@iotv/iotv-v3-types");
const __1 = require("../..");
const DeviceType_1 = require("../../ModelClassInterfaces/DeviceType");
const constants_1 = require("../../constants");
const types_1 = require("../../types");
const GeoModelCommon_1 = require("../Geo/GeoModelCommon");
const typeName = 'Device';
/** TODO: change when DeviceTypes is changed from an enum to a map constructed from external project config outputs */
const DEVICE_TYPE_DROPPABLE = [iotv_v3_types_1.DeviceTypes.RoveTel, iotv_v3_types_1.DeviceTypes.EffluentTracker];
class Device extends GeoModelCommon_1.GeoModelCommon {
    constructor(ob) {
        if (ob.type !== undefined && ob.type !== typeName)
            throw new Error('Tried to create from a non-Device object!');
        super({ ...ob, type: ob.type ?? typeName });
        this.type = ob.type ?? typeName;
        for (let key in ob) {
            this[key] = ob[key];
        }
        this.eui = typeof this.eui === 'string' ? this.eui.toUpperCase() : undefined;
        this.deviceDroppable = ob.deviceDroppable || (ob.deviceType ? DEVICE_TYPE_DROPPABLE.includes(ob.deviceType) : false);
        this.boardVersion = ob.boardVersion ?? null;
        this.batteryRemainingDays = ob.batteryRemainingDays ?? null;
        this.percentageDropRate = ob.percentageDropRate ?? null;
        this.acceptableFailureRate = ob.acceptableFailureRate ?? 10;
        this.beyondAcceptableFailure = ob.beyondAcceptableFailure ?? false;
        this.batteryPercentage = (0, iotv_v3_types_1.isNonNaNNumber)(this.batteryLevel) ? (0, __1.CalculateBatteryFromVoltages)(this.batteryLevel, this.minVoltage, this.maxVoltage) : undefined;
        if (ob.lowPowerModeThreshold && ob.normalPowerModeThreshold) {
            // ensure lowerPowerMode is lower than normal
            this.lowPowerModeThreshold = Math.min(ob.lowPowerModeThreshold, ob.normalPowerModeThreshold);
            this.normalPowerModeThreshold = Math.max(ob.lowPowerModeThreshold, ob.normalPowerModeThreshold);
        }
    }
    validateCompatibility() {
        const validationMap = {
            appEui: (0, iotv_v3_types_1.isValidLoRaEui)(this.appEui),
            appKey: (0, iotv_v3_types_1.isValidLoRaAppKey)(this.appKey),
            /** IoTV requires uppercase EUI for DynamoDB indexing, AWS stores as lowercase in LNS, this is handled in AWS LNS code */
            eui: (0, iotv_v3_types_1.isValidIoTVeui)(this.eui),
            id: this.id === this.eui,
            // 2025 10 - no longer require user to enter a deviceId for AWS LNS
            deviceId: this.networkKey === iotv_v3_types_1.Networks.AWS_LPWAN || !!this.deviceId,
            deviceType: !!this.deviceType,
            networkKey: !!this.networkKey,
            regionalPlan: (0, iotv_v3_types_1.isValidRFRegion)(this.regionalPlan),
        };
        return validationMap;
    }
    isLoRaLNSCompatible() {
        return Object.values(this.validateCompatibility()).every((v) => v === true);
    }
    getInvalidLNSAttributes() {
        const validity = this.validateCompatibility();
        return Object.entries(validity)
            .filter(([k, v]) => !v)
            .map(([k]) => k);
    }
    getStateKeys() {
        let stateAttKeys = [];
        return stateAttKeys;
    }
    set permittedAWOL_MS(awolMs) {
        this._permittedAWOL = awolMs;
    }
    get permittedAWOL_MS() {
        return this._permittedAWOL;
    }
    incrementDownlinkCount() {
        if (!(0, iotv_v3_types_1.isNonNaNNumber)(this.dlCnt)) {
            this.dlCnt = 0;
        }
        this.dlCnt++;
    }
    getIfDeviceDropable() {
        // generic function to be overwritten on parent objects if they have other criterias
        return this.deviceDroppable;
    }
    getDeviceConfigKey() {
        return { pk: this.sk, sk: `${this.sk}_DeviceConfig` };
    }
    getDeviceHistoryKey() {
        return { pk: this.sk, sk: `${this.sk}_DeviceHistory` };
    }
    getUserAttributes() {
        const { name, ...others } = super.getUserAttributes();
        return {
            name: {
                label: 'Name',
                type: 'String',
                editable: true,
                required: true,
            },
            eui: {
                type: 'String',
                editable: false,
                required: true,
                label: 'Device EUI',
                name: 'eui',
                validation: { regex: iotv_v3_types_1.CommonRegexMap.LoRa.eui, pass: true, invalidMessage: 'Device EUI must be 8 hexadecimal bytes' },
                valueFn: (newValue) => (newValue ? newValue.toUpperCase().replace(/\s/g, '') : ''),
                displayFn: (val) => {
                    return val?.replace(/([0-9a-fA-F]{2})(?!=(\s|$))/g, '$1 ')?.toUpperCase() ?? '';
                },
            },
            deviceId: {
                type: 'String',
                required: true,
                editable: true,
                label: 'Device ID',
                name: 'deviceId',
                dependsOn: (item) => {
                    const partialVKD = item.networkKey !== iotv_v3_types_1.Networks.AWS_LPWAN
                        ? {
                            required: item.networkKey === iotv_v3_types_1.Networks.TTN_Enterprise,
                        }
                        : { hidden: true };
                    return partialVKD;
                },
                // validation: { regex: '/^\S{4,}/', pass: true, invalidMessage: 'Device Id must be present ' },
            },
            appID: {
                type: 'String',
                editable: true,
                required: true,
                label: 'App ID',
                name: 'appID', // default true/true to appear by
                dependsOn: (item) => {
                    const partialVKD = item.networkKey === iotv_v3_types_1.Networks.AWS_LPWAN
                        ? {
                            required: true,
                            editable: !item.appID || (item.eui && item.eui !== item.id), // the second part of this uses a sideeffect to determine whether the item is new and the user should be allowed to change the lnsIntanceType
                            type: 'Enum',
                            enumKv: { LOCAL: 'LOCAL', LNC: 'LNC', LNC_HTTP: 'LNC_HTTP' },
                        }
                        : { required: false };
                    return partialVKD;
                },
            },
            networkKey: { type: 'Enum', enumKv: iotv_v3_types_1.Networks, editable: false, label: 'Application Server', name: 'networkKey', default: iotv_v3_types_1.Networks.AWS_LPWAN },
            regionalPlan: {
                name: 'regionalPlan',
                type: 'Enum',
                required: true,
                editable: this.regionalPlan === undefined,
                enumKv: iotv_v3_types_1.RFRegions,
                label: 'RF Region',
            },
            appEui: {
                type: 'String',
                required: false,
                editable: false,
                label: 'App/Join EUI',
                name: 'appEui',
                validation: { regex: iotv_v3_types_1.CommonRegexMap.IoTV.eui, pass: true, invalidMessage: 'App/Join EUI must be 8 hexadecimal bytes' },
                /** to do, the validation process should regex on the value of valueFn if present, otherwise can't support pretty hex */
                valueFn: (newValue) => (newValue ? newValue.toUpperCase().replace(/\s/g, '') : ''),
                displayFn: (val) => {
                    return val?.replace(/([0-9a-fA-F]{2})(?!=(\s|$))/g, '$1 ')?.toUpperCase() ?? '';
                },
            },
            appKey: {
                type: 'String',
                required: false,
                editable: false,
                label: 'App Key',
                name: 'appKey',
                validation: { regex: iotv_v3_types_1.CommonRegexMap.LoRa.appKey, pass: true, invalidMessage: 'App Key must be 16 hexadecimal bytes' },
                /** to do, the validation process should regex on the value of valueFn if present, otherwise can't support pretty hex */
                valueFn: (newValue) => (newValue ? newValue.toUpperCase().replace(/\s/g, '') : ''),
                displayFn: (val) => {
                    return val?.replace(/([0-9a-fA-F]{2})(?!=(\s|$))/g, '$1 ')?.toUpperCase() ?? '';
                },
            },
            deviceType: {
                type: 'Enum',
                editable: true,
                label: 'Device Type',
                name: 'deviceType',
                enumKv: Object.fromEntries(Object.entries(iotv_v3_types_1.DeviceTypes)
                    .filter(([k, v]) => !isNaN(parseInt(v)))
                    .map(([k, v]) => [iotv_v3_types_1.DeviceTypeNames[v], v])),
            },
            gatewayChanged: { type: 'Boolean', required: false, editable: false, label: 'Gateway Changed', name: 'Gateway Changed', default: false },
            deviceSubType: { type: 'String', editable: true, label: 'Device Subtype', name: 'deviceSubType' },
            deviceDroppable: { type: 'Boolean', required: false, editable: false, label: 'Droppable', name: 'deviceDroppable', default: false },
            deviceVersion: { type: 'String', editable: true, label: 'Device Version', name: 'deviceVersion' },
            firmwareLevel: { type: 'String', editable: false, label: 'Firmware Level', name: 'firmwareLevel' },
            ...others,
            batteryPercentage: { type: 'Number', editable: false, label: 'Battery', units: '%', name: 'batteryPercentage' },
            batteryLevel: { type: 'Number', editable: false, label: 'Battery', units: 'V', name: 'batteryLevel' },
            minVoltage: {
                type: 'Number',
                editable: true,
                label: 'Minimum Battery',
                units: 'V',
                name: 'minVoltage',
            },
            batteryRemainingDays: {
                type: 'Number',
                editable: false,
                label: 'Battery Days Remaining',
                name: 'batteryRemainingDays',
            },
            percentageDropRate: {
                type: 'Number',
                editable: false,
                label: 'Battery Percentage Drop Per Day',
                name: 'percentageDropRate',
            },
            maxVoltage: {
                type: 'Number',
                editable: true,
                label: 'Maximum Battery',
                units: 'V',
                name: 'maxVoltage',
            },
            lowPowerModeThreshold: {
                type: 'Number',
                editable: true,
                label: 'Low Battery Threshold',
                default: undefined,
                units: 'V',
                name: 'lowPowerModeThreshold',
            },
            batteryCapacity: { type: 'Number', editable: false, label: 'Battery Capacity', units: 'mAh', name: 'batteryCapacity' },
            normalPowerModeThreshold: {
                type: 'Number',
                editable: true,
                label: 'Normal Battery Threshold',
                default: undefined,
                units: 'V',
                name: 'normalPowerModeThreshold',
            },
            lastSeen: {
                type: 'Date',
                editable: false,
                label: 'Last Seen',
                name: 'lastSeen',
                precision: types_1.UserAttributeKeyDatePrecision.DateTimeSecs,
                // displayFn: (actualValue: number | undefined) => (actualValue ? toDatePickerFormat(actualValue) : undefined),
                // valueFn: (newValue: number | undefined) => (newValue ? new Date(newValue).valueOf() : undefined),
            },
            overdue: { type: 'Boolean', editable: false, label: 'Overdue', name: 'Overdue' },
            isAWOL: { type: 'Boolean', editable: false, label: 'AWOL', name: 'isAWOL' },
            _permittedAWOL: {
                type: 'Number',
                editable: true,
                label: 'AWOL after',
                units: 'minutes',
                name: '_permittedAWOL',
                displayFn: (actualValue) => (actualValue ? actualValue / constants_1.Milliseconds.MINUTE : undefined),
                valueFn: (newValue) => (newValue ? newValue * constants_1.Milliseconds.MINUTE : undefined),
            },
            state: {
                type: 'Enum',
                editable: true,
                label: 'State',
                name: 'State',
                enumKv: DeviceType_1.DeviceState,
                valueFn: (c) => DeviceType_1.DeviceState[c] ?? c,
                default: DeviceType_1.DeviceState.INVENTORY,
            },
            almanacAge: { type: 'Number', editable: false, label: 'Almanac Age', name: 'Almanac Age' },
            customer: {
                type: 'String',
                editable: false,
                label: 'Customer',
                name: 'customer',
            },
            boardVersion: { type: 'String', editable: true, label: 'Board Version', name: 'boardVersion' },
            billingGroup: { type: 'String', editable: false, label: 'Billing Group', name: 'billingGroup' },
            provisioning: {
                type: 'object',
                editable: false,
                label: 'Provisioning',
                name: 'provisioning',
                displayFn: (actualValue) => {
                    if (actualValue == undefined || actualValue.length === 0)
                        return 'Never provisioned';
                    return `Provisioned ${actualValue.length} time${actualValue.length !== 1 ? 's' : ''}`;
                },
            },
            regionalParameterRevision: {
                type: 'Enum',
                editable: true,
                label: 'Regional Params',
                name: 'regionalParameterRevision',
                enumKv: types_1.RegionalParameterRevision,
            },
            lastSendPeriod: {
                type: 'Number',
                editable: false,
                label: 'Message Period Actual',
                units: 'minutes',
                name: 'lastSendPeriod',
                precision: 2,
                displayFn: (actualValue) => (actualValue ? actualValue / constants_1.Milliseconds.MINUTE : undefined),
            },
            fCnt: { type: 'Number', editable: false, label: 'Framecount', name: 'fCnt' },
            fCntInc: { type: 'Number', editable: false, label: 'Framecount Increment', name: 'fCntInc' },
            dlCnt: { type: 'Number', editable: false, label: 'DL Count', name: 'dlCnt' },
            //Messages: {type:"UNKNOWN",editable:true,label:"//Messages",name:"//Messages"},
            messageJoinCount: { type: 'Number', editable: false, label: 'Joins', name: 'messageJoinCount' },
            messageUplinkCount: { type: 'Number', editable: false, label: 'Uplinks', name: 'messageUplinkCount' },
            messageDownlinkCount: { type: 'Number', editable: false, label: 'Downlinks', name: 'messageDownlinkCount' },
            messageFailureRate: { type: 'Number', editable: false, label: 'Failure Rate', name: 'messageFailureRate' },
            acceptableFailureRate: { type: 'Number', editable: true, label: 'Acceptable Failure Rate', name: 'acceptableFailureRate' },
            beyondAcceptableFailure: {
                type: 'Boolean',
                required: false,
                editable: false,
                label: 'Beound Acceptable Failures',
                name: 'beyondAcceptableFailure',
                default: false,
            },
            messageLast: { type: 'Number', editable: false, label: 'Last Message', name: 'messageLast' },
            messagePeriodDefault: { type: 'Number', editable: true, label: 'Default Period', name: 'messagePeriodDefault' },
            messagePeriodActual: { type: 'Number', editable: false, label: 'Actual Period', name: 'messagePeriodActual' },
            dailyMessageCount: { type: 'Number', editable: false, label: 'Daily Message Count', name: 'dailyMessageCount' },
            dailyMessageHistory: { type: 'Number', editable: false, label: 'Daily Message Count History', name: 'dailyMessageHistory' },
            dailyFCntSkip: { type: 'Number', editable: false, label: 'Daily Framecount Skip', name: 'dailyFCntSkip' },
            fCntSkipHistory: { type: 'Number', editable: false, label: 'Framecount Skip History', name: 'fCntSkipHistory' },
            yesterdaysMessageCntTotal: { type: 'Number', editable: false, label: "Yesterday's Message Count", name: 'yesterdaysMessageCntTotal' },
            yesterdaysFCntJumpTotal: { type: 'Number', editable: false, label: "Yesterday's Framecount Skip", name: 'yesterdaysFCntJumpTotal' },
        };
    }
}
exports.Device = Device;
